/**
 * AdvisorAccessPanel — Widerrufs-UI für aktive Kanzlei-Zugriffe (B5,
 * architecture-stb-onboarding-consent.md §7.5).
 *
 * Listet alle Kanzleien, die aktuell Lesezugriff auf den EIGENEN Tenant des
 * eingeloggten Mandanten/Owners haben (`tenant_members` mit
 * `scopeUid == uid && role == 'advisor' && status == 'active'`), und erlaubt den
 * protokollierten Widerruf über {@link revokeAdvisorAccess}. Pflicht-Bestandteil
 * der B3-Welle: der §3-Consent-Text verspricht den Widerruf „in den Einstellungen",
 * daher wird das Panel für JEDEN Owner gerendert (nicht feature-gated). Owner ohne
 * aktive Advisor-Kanten sehen einen Leerzustand — für die meisten Nutzer inert.
 *
 * Fail-safe: jeder Firestore-/Callable-Fehler wird abgefangen und als lesbare
 * Meldung angezeigt (kein stiller Fehler, kein Crash). Der Widerruf verlangt eine
 * ausdrückliche Bestätigung (Confirm-Dialog, Kopplungsverbot an einen Fehlklick).
 *
 * Namensauflösung: `advisor_profiles/{advisorUid}` ist rules-seitig get-own
 * (der Owner darf das Kanzlei-Profil NICHT lesen) und die Membership-Kante trägt
 * keine E-Mail — die Fallback-Kette (Kanzlei-Name → E-Mail → UID) endet daher in
 * der Praxis bei der Advisor-UID. Der Profil-Read bleibt dennoch als bester-
 * Aufwand-Versuch erhalten (falls Rules künftig gelockert werden), Fehler werden
 * geschluckt.
 *
 * @module components/tenant/AdvisorAccessPanel
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { IconBuilding, IconCheck, IconVoid, IconWarning } from '../../icons';
import { grantAdvisorConsent, revokeAdvisorAccess } from '../../api/advisorAccessApi';
import AdvisorConsentDialog from './AdvisorConsentDialog';
import logger from '../../utils/logger';

/** Eine aktive Advisor-Kante (Kanzlei mit Lesezugriff auf den eigenen Tenant). */
interface AdvisorEdge {
  /** Membership-Doc-Id (`{scopeUid}__{advisorUid}`). */
  membershipId: string;
  /** uid der Kanzlei (== `memberUid` der Kante). */
  advisorUid: string;
  /** Anzeigename (Fallback-Kette → i.d.R. die Advisor-UID). */
  displayName: string;
  /** Einwilligungs-Zeitpunkt (Firestore-Timestamp) oder null, falls (noch) nicht gesetzt. */
  consentAt: Date | null;
}

/**
 * Normalisiert einen Firestore-Timestamp-ähnlichen Wert defensiv zu einem `Date`
 * (oder null). Akzeptiert echte Firestore-Timestamps (`toDate()`), `{seconds}`-
 * Objekte und ISO-Strings; alles andere ⇒ null.
 */
function toDateSafe(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && typeof (value as { toDate?: unknown }).toDate === 'function') {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'object' && value !== null && typeof (value as { seconds?: unknown }).seconds === 'number') {
    const d = new Date((value as { seconds: number }).seconds * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Best-effort-Auflösung des Kanzlei-Namens. Versucht `advisor_profiles/{advisorUid}`
 * (rules-seitig i.d.R. nicht owner-lesbar ⇒ Fehler wird geschluckt) und fällt sonst
 * auf die Advisor-UID zurück. Wirft NIE.
 */
async function resolveKanzleiDisplayName(advisorUid: string): Promise<string> {
  try {
    const snap = await getDoc(doc(db, 'advisor_profiles', advisorUid));
    if (snap.exists()) {
      const name = snap.data()?.kanzleiName;
      if (typeof name === 'string' && name.trim().length > 0) {
        return name.trim();
      }
    }
  } catch (err) {
    // Erwartet für Owner (get-own-Rule) — kein harter Fehler, nur Debug.
    logger.debug('[AdvisorAccessPanel] advisor_profiles not readable, falling back to uid', err);
  }
  return advisorUid;
}

/**
 * Panel „Zugriff der Kanzleien" auf der {@link AdvisorPortalPage}. Für jeden Owner
 * sichtbar; zeigt Leerzustand ohne aktive Kanten.
 */
const AdvisorAccessPanel: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const { currentUser } = useAuth();

  const [edges, setEdges] = useState<AdvisorEdge[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);

  // Widerruf-Confirm-Zustand: die zu widerrufende Kante (null ⇒ kein Dialog).
  const [confirmTarget, setConfirmTarget] = useState<AdvisorEdge | null>(null);
  const [revoking, setRevoking] = useState<boolean>(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  // Einwilligungs-Zustand (F1): die Kante, für die der §3-Consent-Dialog offen ist.
  const [grantTarget, setGrantTarget] = useState<AdvisorEdge | null>(null);
  const [granting, setGranting] = useState<boolean>(false);
  const [grantError, setGrantError] = useState<string | null>(null);

  /**
   * Lädt die aktiven Advisor-Kanten des eigenen Tenants. Fail-safe: bei jedem
   * Fehler leere Liste + Fehler-Flag (kein Crash, kein fremder Zugriff).
   */
  const loadEdges = useCallback(async (uid: string) => {
    setLoading(true);
    setLoadError(false);
    try {
      const membersRef = collection(db, 'tenant_members');
      // Reine Gleichheits-Filter ⇒ kein Composite-Index nötig (Zig-Zag-Merge).
      const q = query(
        membersRef,
        where('scopeUid', '==', uid),
        where('role', '==', 'advisor'),
        where('status', '==', 'active'),
      );
      const snap = await getDocs(q);

      const rows: AdvisorEdge[] = [];
      for (const memberDoc of snap.docs) {
        const data = memberDoc.data();
        const advisorUid = typeof data.memberUid === 'string' ? data.memberUid : null;
        // Ungültige Kante ohne Advisor-uid überspringen (defensiv).
        if (!advisorUid) continue;
        // F1: der server-denormalisierte `kanzleiNameShown` ist die primäre Quelle
        // (Panel zeigt Kanzleiname statt roher UID). Fällt er (Altkante) weg, greift
        // die Best-effort-Kette (Profil-Read → UID).
        const denormName =
          typeof data.kanzleiNameShown === 'string' && data.kanzleiNameShown.trim().length > 0
            ? data.kanzleiNameShown.trim()
            : null;
        const displayName = denormName ?? (await resolveKanzleiDisplayName(advisorUid));
        rows.push({
          membershipId: memberDoc.id,
          advisorUid,
          displayName,
          consentAt: toDateSafe(data.mandantConsentAt),
        });
      }
      setEdges(rows);
    } catch (err) {
      logger.error('[AdvisorAccessPanel] Failed to load active advisor edges', err);
      setEdges([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) {
      setEdges([]);
      setLoading(false);
      return;
    }
    void loadEdges(currentUser.uid);
  }, [currentUser?.uid, loadEdges]);

  /**
   * Führt den bestätigten Widerruf aus. Optimistisch: entfernt die Kante nach
   * Erfolg aus der Liste. Bei Fehler bleibt der Dialog offen und zeigt die Meldung.
   */
  const handleConfirmRevoke = useCallback(async () => {
    if (!confirmTarget) return;
    setRevoking(true);
    setRevokeError(null);
    try {
      await revokeAdvisorAccess(confirmTarget.advisorUid);
      setEdges((prev) => prev.filter((e) => e.membershipId !== confirmTarget.membershipId));
      setConfirmTarget(null);
    } catch (err) {
      logger.error('[AdvisorAccessPanel] revokeAdvisorAccess failed', err);
      const message = err instanceof Error && err.message ? err.message : t('advisorAccess.revokeError');
      setRevokeError(message);
    } finally {
      setRevoking(false);
    }
  }, [confirmTarget, t]);

  /**
   * Erteilt die Einwilligung nach Bestätigung im §3-Dialog. Bei Erfolg: Dialog
   * schließen + Panel neu laden (zeigt dann Kanzleiname + Einwilligungsdatum). Bei
   * Fehler bleibt der Dialog offen und zeigt die Meldung.
   */
  const handleConfirmGrant = useCallback(async () => {
    if (!grantTarget || !currentUser?.uid) return;
    setGranting(true);
    setGrantError(null);
    try {
      await grantAdvisorConsent({
        advisorUid: grantTarget.advisorUid,
        kanzleiName: grantTarget.displayName,
      });
      setGrantTarget(null);
      await loadEdges(currentUser.uid);
    } catch (err) {
      logger.error('[AdvisorAccessPanel] grantAdvisorConsent failed', err);
      const message = err instanceof Error && err.message ? err.message : t('advisorAccess.grantError');
      setGrantError(message);
    } finally {
      setGranting(false);
    }
  }, [grantTarget, currentUser?.uid, loadEdges, t]);

  const formatConsentDate = (date: Date): string => {
    try {
      return date.toLocaleDateString(i18n.language || 'de', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return date.toISOString().slice(0, 10);
    }
  };

  return (
    <section
      className="mb-6 rounded-lg bg-surface-raised ring-1 ring-black ring-opacity-5 p-4"
      data-testid="advisor-access-panel"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
        {t('advisorAccess.title')}
      </h2>
      <p className="mt-1 text-sm text-secondary leading-relaxed">
        {t('advisorAccess.intro')}
      </p>

      {loading ? (
        <p className="mt-4 text-sm text-faint" data-testid="advisor-access-loading">
          {t('advisorAccess.loading')}
        </p>
      ) : loadError ? (
        <p className="mt-4 text-sm text-[var(--tone-danger-fg)]" data-testid="advisor-access-load-error">
          {t('advisorAccess.loadError')}
        </p>
      ) : edges.length === 0 ? (
        <p className="mt-4 text-sm text-faint" data-testid="advisor-access-empty">
          {t('advisorAccess.empty')}
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-hairline" data-testid="advisor-access-list">
          {edges.map((edge) => (
            <li
              key={edge.membershipId}
              className="flex flex-wrap items-center justify-between gap-3 py-3"
              data-testid="advisor-access-row"
            >
              <div className="flex min-w-0 items-start gap-2">
                <span className="mt-0.5 shrink-0 text-muted">
                  <IconBuilding size={16} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-primary" title={edge.displayName}>
                    {edge.displayName}
                  </p>
                  {edge.consentAt ? (
                    <span
                      className="mt-0.5 inline-flex items-center gap-1 text-xs text-[var(--tone-success-fg)]"
                      data-testid="advisor-access-consent-given"
                    >
                      <IconCheck size={12} />
                      {t('advisorAccess.consentDate', { date: formatConsentDate(edge.consentAt) })}
                    </span>
                  ) : (
                    <span
                      className="mt-0.5 inline-flex items-center gap-1 text-xs text-[var(--tone-warning-fg)]"
                      data-testid="advisor-access-consent-pending"
                    >
                      <IconWarning size={12} />
                      {t('advisorAccess.consentPending')}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {!edge.consentAt && (
                  <button
                    type="button"
                    onClick={() => {
                      setGrantError(null);
                      setGrantTarget(edge);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-sm font-medium text-accent-fg hover:bg-indigo-700 active:bg-indigo-700"
                    data-testid="advisor-access-grant"
                  >
                    <IconCheck size={14} />
                    {t('advisorAccess.grantButton')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setRevokeError(null);
                    setConfirmTarget(edge);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-surface-inset px-3 py-1.5 text-sm font-medium text-[var(--tone-danger-fg)] hover:bg-surface-hover"
                  data-testid="advisor-access-revoke"
                >
                  <IconVoid size={14} />
                  {t('advisorAccess.revokeButton')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {confirmTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="advisor-access-confirm-title"
          data-testid="advisor-access-confirm"
        >
          <div className="w-full max-w-md rounded-xl border border-hairline bg-surface-raised p-6 shadow-2xl">
            <div className="flex items-start gap-2">
              <span className="mt-0.5 shrink-0 text-[var(--tone-warning-fg)]">
                <IconWarning />
              </span>
              <div>
                <h3
                  id="advisor-access-confirm-title"
                  className="text-lg font-semibold text-primary"
                >
                  {t('advisorAccess.confirmTitle')}
                </h3>
                <p className="mt-1 text-sm text-secondary">
                  {t('advisorAccess.confirmText', { kanzlei: confirmTarget.displayName })}
                </p>
              </div>
            </div>

            {revokeError && (
              <p
                className="mt-3 text-sm text-[var(--tone-danger-fg)]"
                data-testid="advisor-access-revoke-error"
              >
                {revokeError}
              </p>
            )}

            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setConfirmTarget(null);
                  setRevokeError(null);
                }}
                disabled={revoking}
                className="inline-flex items-center rounded-md bg-surface-inset px-3 py-1.5 text-sm font-medium text-secondary hover:bg-surface-hover disabled:opacity-50"
                data-testid="advisor-access-confirm-cancel"
              >
                {t('advisorAccess.confirmCancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmRevoke}
                disabled={revoking}
                className="inline-flex items-center gap-1.5 rounded-md bg-[var(--tone-danger-fg)] px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="advisor-access-confirm-revoke"
              >
                {revoking ? t('advisorAccess.revoking') : t('advisorAccess.confirmRevoke')}
              </button>
            </div>
          </div>
        </div>
      )}

      {grantTarget && (
        <AdvisorConsentDialog
          kanzleiName={grantTarget.displayName}
          submitting={granting}
          error={grantError}
          onGrant={handleConfirmGrant}
          onCancel={() => {
            setGrantTarget(null);
            setGrantError(null);
          }}
        />
      )}
    </section>
  );
};

export default AdvisorAccessPanel;
